import { useEffect, useRef, useState } from 'react'

type LoadingProgressControllerResult = {
    // Progress the bar animates towards, it runs ahead of what is on screen
    displayProgress: number
    durationMS: number
    // True once the bar has visually arrived at 100 %
    isComplete: boolean
}

// How often the phase is re-evaluated. This does not re-render on its own, a new
// state is only pushed when the target or the duration actually changed
const STEP_MS = 200
// A blocked main thread can produce huge gaps between steps, don't jump then
const MAX_STEP_MS = 1000

// Up to here the bar runs on the time estimate that adapts to the download progress
const HANDOVER_PROGRESS = 60
const CRAWL_DISTANCE = 100 - HANDOVER_PROGRESS

// Time the estimate would need for a full bar, only the part below HANDOVER_PROGRESS is used
const ESTIMATE_DURATION_MS = 12000
// Downloads are running ahead of the bar
const ESTIMATE_DURATION_AHEAD_MS = 10000
// Downloads are running behind the bar
const ESTIMATE_DURATION_BEHIND_MS = 20000
// Downloads are done
const ESTIMATE_DURATION_DONE_MS = 3000
// Before this the estimate is not adapted yet
const ADAPT_AFTER_MS = 5000

// From HANDOVER_PROGRESS the bar crawls to 100 % at a constant speed. Covers the
// time the scene needs to transcode textures, compile shaders and push its first
// frame, which is where slow Android devices spend several seconds
export const DEFAULT_CRAWL_DURATION_MS = 25000
// Card landing waits on a 3D card reveal after assets are ready
export const CARD_LANDING_CRAWL_DURATION_MS = 35000

// Everything is ready earlier: time for the full crawl distance when covering the rest
const FINISH_DURATION_MS = 800

const initialState: LoadingProgressControllerResult = {
    displayProgress: 0,
    durationMS: 0,
    isComplete: false,
}

/**
 * Drives the loading bar. `isReady` should be the same condition that dismisses
 * the loading screen, so a full bar always means "we are leaving now" instead of
 * "still waiting". Falls back to the download progress when no readiness signal
 * is available.
 *
 * `LoadingBar` restarts its animation whenever the target changes and only
 * retimes the running one when the duration changes, so a phase keeps a fixed
 * target and steers via the duration alone. Restarting per step would resample
 * the compositor driven transform and make the bar stutter.
 */
export function useLoadingProgressController(
    assetProgress: number,
    isReady?: boolean,
    crawlDurationMs = DEFAULT_CRAWL_DURATION_MS,
): LoadingProgressControllerResult {
    const assetProgressRef = useRef(assetProgress)
    const isReadyRef = useRef(isReady)
    const crawlDurationMsRef = useRef(crawlDurationMs)
    const [state, setState] = useState(initialState)

    useEffect(() => {
        assetProgressRef.current = assetProgress
    }, [assetProgress])

    useEffect(() => {
        isReadyRef.current = isReady
    }, [isReady])

    useEffect(() => {
        crawlDurationMsRef.current = crawlDurationMs
    }, [crawlDurationMs])

    useEffect(() => {
        const startMs = performance.now()
        let lastStepMs = startMs
        // Where the bar stands, tracked along with the animation so the phases can
        // be switched without reading it back from the DOM
        let position = 0
        let finish: { fromProgress: number, startedAtMs: number, durationMs: number } | null = null
        let committed = initialState

        const commit = (displayProgress: number, durationMS: number, isComplete = false) => {
            if (
                committed.displayProgress === displayProgress
                && committed.durationMS === durationMS
                && committed.isComplete === isComplete
            ) {
                return
            }

            committed = { displayProgress, durationMS, isComplete }
            setState(committed)
        }

        const estimateProgressPerMs = (elapsedMs: number) => {
            if (elapsedMs <= ADAPT_AFTER_MS) return 100 / ESTIMATE_DURATION_MS
            if (assetProgressRef.current >= 100) return 100 / ESTIMATE_DURATION_DONE_MS
            if (assetProgressRef.current > elapsedMs / 100) return 100 / ESTIMATE_DURATION_AHEAD_MS
            return 100 / ESTIMATE_DURATION_BEHIND_MS
        }

        const step = () => {
            const now = performance.now()
            const deltaMs = Math.min(now - lastStepMs, MAX_STEP_MS)
            lastStepMs = now

            const progressPerMs = position < HANDOVER_PROGRESS
                ? estimateProgressPerMs(now - startMs)
                : CRAWL_DISTANCE / crawlDurationMsRef.current
            position = Math.min(100, position + progressPerMs * deltaMs)

            const isReadyNow = isReadyRef.current ?? assetProgressRef.current >= 100

            // Finishing early is only allowed once the estimate has handed over,
            // otherwise a warm cache would make the splash flash by
            if (isReadyNow && position >= HANDOVER_PROGRESS) {
                if (finish === null) {
                    const remaining = Math.max(0, 100 - position)
                    finish = {
                        fromProgress: position,
                        startedAtMs: now,
                        durationMs: (remaining / CRAWL_DISTANCE) * FINISH_DURATION_MS,
                    }
                }

                const sweep = finish.durationMs > 0
                    ? Math.min(1, (now - finish.startedAtMs) / finish.durationMs)
                    : 1
                position = finish.fromProgress + (100 - finish.fromProgress) * sweep

                commit(100, FINISH_DURATION_MS, sweep >= 1)

                if (sweep >= 1) {
                    window.clearInterval(intervalId)
                }
                return
            }

            if (position < HANDOVER_PROGRESS) {
                // Same speed as a full bar over the estimate, just cut off at the handover
                commit(HANDOVER_PROGRESS, HANDOVER_PROGRESS / estimateProgressPerMs(now - startMs))
                return
            }

            commit(100, crawlDurationMsRef.current)
        }

        const intervalId = window.setInterval(step, STEP_MS)
        return () => window.clearInterval(intervalId)
    }, [])

    return state
}
